import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui";
import { LoadingState, ErrorState, SectionCard } from "@/components/common";
import { profileService, type UserLegalInfo } from "@/services/profileService";
import { ProfileSettingsForm } from "@/components/profile";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import { AdminUserLegalInfoReadView } from "./AdminUserLegalInfoReadView";

interface AdminUserLegalInfoSectionProps {
  userId: string;
  accountEmail: string;
  embedded?: boolean;
}

export function AdminUserLegalInfoSection({
  userId,
  accountEmail,
  embedded = false,
}: AdminUserLegalInfoSectionProps) {
  const { t } = useTranslation("adminUsers");
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);

  const canEdit = hasPermission(Permission.USERS_UPDATE_ANY);

  const {
    data: profile,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["admin", "user-legal-info", userId],
    queryFn: () => profileService.getProfile(userId),
    enabled: !!userId,
  });

  const invalidateRelatedQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "seller", userId] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "host", userId] });
    void queryClient.invalidateQueries({
      queryKey: ["admin", "sales-agents", userId],
    });
  }, [queryClient, userId]);

  const handleProfileUpdated = useCallback(
    (updated: UserLegalInfo) => {
      queryClient.setQueryData(["admin", "user-legal-info", userId], updated);
      invalidateRelatedQueries();
    },
    [queryClient, userId, invalidateRelatedQueries],
  );

  const handleCancelEdit = () => {
    setIsEditing(false);
    void refetch();
  };

  const editActions =
    canEdit && profile ? (
      <div className="flex shrink-0 gap-2">
        {isEditing ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancelEdit}
            leftIcon={<X className="h-4 w-4" />}
          >
            {t("detail.cancelEditLegalInfo")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
            leftIcon={<Pencil className="h-4 w-4" />}
          >
            {t("detail.editLegalInfo")}
          </Button>
        )}
      </div>
    ) : null;

  const content = isLoading ? (
    <LoadingState message={t("detail.loadLegalInfo")} />
  ) : isError || !profile ? (
    <ErrorState
      message={t("detail.loadLegalInfoError")}
      onRetry={() => void refetch()}
    />
  ) : isEditing ? (
    <ProfileSettingsForm
      key={`${userId}-${profile.id}-${profile.profile_type}-edit`}
      profile={profile}
      userEmail={accountEmail}
      userId={userId}
      onProfileUpdated={handleProfileUpdated}
      showSectionNav={false}
    />
  ) : (
    <AdminUserLegalInfoReadView
      profile={profile}
      toolbarAction={embedded ? editActions : undefined}
    />
  );

  if (embedded) {
    return (
      <div>
        {isEditing && editActions ? (
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-surface-900 text-[15px] font-semibold tracking-tight">
              {t("detail.editLegalInfo")}
            </p>
            {editActions}
          </div>
        ) : null}
        {content}
      </div>
    );
  }

  return (
    <SectionCard
      title={t("detail.legalInfoCard")}
      icon={FileText}
      iconTone="plain"
      iconClassName="text-surface-400"
      action={editActions}
      className="mb-8"
    >
      {content}
    </SectionCard>
  );
}
